import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ToolFixture,
  Supplier,
  Department,
  ToolType,
  CalibrationCalendarDetail,
  SearchToolFixturesRequest,
  SearchToolFixturesResponse,
  CreateToolFixtureRequest,
  UpdateToolFixtureRequest,
  CreateCalibrationRequest,
  UpdateCalibrationRequest,
  ToolFixtureStatistics,
} from '../../services/toolFixtureApi';

// State Interface
export interface ToolFixtureState {
  // Tool Fixtures
  toolFixtures: ToolFixture[];
  toolFixturesLoading: boolean;
  toolFixturesError: string | null;
  
  // Selected Tool Fixture
  selectedToolFixture: ToolFixture | null;
  selectedToolFixtureLoading: boolean;
  selectedToolFixtureError: string | null;
  
  // Calibration Events
  calibrationEvents: CalibrationCalendarDetail[];
  calibrationEventsLoading: boolean;
  calibrationEventsError: string | null;
  
  // Create/Update Operations
  createToolFixtureLoading: boolean;
  createToolFixtureError: string | null;
  updateToolFixtureLoading: boolean;
  updateToolFixtureError: string | null;
  deleteToolFixtureLoading: boolean;
  deleteToolFixtureError: string | null;
  
  // Calibration Operations
  createCalibrationLoading: boolean;
  createCalibrationError: string | null;
  updateCalibrationLoading: boolean;
  updateCalibrationError: string | null;
  deleteCalibrationLoading: boolean;
  deleteCalibrationError: string | null;
  
  // Dropdown Data
  suppliers: Supplier[];
  departments: Department[];
  toolTypes: ToolType[];
  dropdownsLoading: boolean;
  dropdownsError: string | null;
  
  // Pagination & Filters
  pagination: {
    pageNum: number;
    pageSize: number;
    totalCount: number;
  };
  searchTerm: string;
  filters: {
    calibrationRequired?: boolean;
    active?: boolean;
    supplierId?: string;
    departmentId?: string;
    toolTypeId?: string;
  };
  
  // Statistics
  statistics: ToolFixtureStatistics | null;
  statisticsLoading: boolean;
  statisticsError: string | null;
  
  // UI State
  validationErrors: string[];
  detailModalOpen: boolean;
  deleteDialogOpen: boolean;
  calibrationDialogOpen: boolean;
  toolFixtureToDelete: ToolFixture | null;
  selectedCalibrationEvent: CalibrationCalendarDetail | null;
}

// Initial State
const initialState: ToolFixtureState = {
  // Tool Fixtures
  toolFixtures: [],
  toolFixturesLoading: false,
  toolFixturesError: null,
  
  // Selected Tool Fixture
  selectedToolFixture: null,
  selectedToolFixtureLoading: false,
  selectedToolFixtureError: null,
  
  // Calibration Events
  calibrationEvents: [],
  calibrationEventsLoading: false,
  calibrationEventsError: null,
  
  // Create/Update Operations
  createToolFixtureLoading: false,
  createToolFixtureError: null,
  updateToolFixtureLoading: false,
  updateToolFixtureError: null,
  deleteToolFixtureLoading: false,
  deleteToolFixtureError: null,
  
  // Calibration Operations
  createCalibrationLoading: false,
  createCalibrationError: null,
  updateCalibrationLoading: false,
  updateCalibrationError: null,
  deleteCalibrationLoading: false,
  deleteCalibrationError: null,
  
  // Dropdown Data
  suppliers: [],
  departments: [],
  toolTypes: [],
  dropdownsLoading: false,
  dropdownsError: null,
  
  // Pagination & Filters
  pagination: {
    pageNum: 1,
    pageSize: 25,
    totalCount: 0,
  },
  searchTerm: '',
  filters: {},
  
  // Statistics
  statistics: null,
  statisticsLoading: false,
  statisticsError: null,
  
  // UI State
  validationErrors: [],
  detailModalOpen: false,
  deleteDialogOpen: false,
  calibrationDialogOpen: false,
  toolFixtureToDelete: null,
  selectedCalibrationEvent: null,
};

// Redux Slice
const toolFixtureSlice = createSlice({
  name: 'toolFixture',
  initialState,
  reducers: {
    // Tool Fixtures Actions
    fetchToolFixturesStart: (state, action: PayloadAction<SearchToolFixturesRequest | undefined>) => {
      state.toolFixturesLoading = true;
      state.toolFixturesError = null;
    },
    fetchToolFixturesSuccess: (state, action: PayloadAction<SearchToolFixturesResponse>) => {
      state.toolFixturesLoading = false;
      state.toolFixtures = action.payload.items;
      state.pagination = {
        pageNum: action.payload.pageNum,
        pageSize: action.payload.pageSize,
        totalCount: action.payload.totalCount,
      };
    },
    fetchToolFixturesFailure: (state, action: PayloadAction<string>) => {
      state.toolFixturesLoading = false;
      state.toolFixturesError = action.payload;
    },

    // Selected Tool Fixture Actions
    fetchToolFixtureByIdStart: (state, action: PayloadAction<string>) => {
      state.selectedToolFixtureLoading = true;
      state.selectedToolFixtureError = null;
    },
    fetchToolFixtureByIdSuccess: (state, action: PayloadAction<ToolFixture>) => {
      state.selectedToolFixtureLoading = false;
      state.selectedToolFixture = action.payload;
    },
    fetchToolFixtureByIdFailure: (state, action: PayloadAction<string>) => {
      state.selectedToolFixtureLoading = false;
      state.selectedToolFixtureError = action.payload;
    },

    // Create Tool Fixture Actions
    createToolFixtureStart: (state, action: PayloadAction<CreateToolFixtureRequest>) => {
      state.createToolFixtureLoading = true;
      state.createToolFixtureError = null;
    },
    createToolFixtureSuccess: (state, action: PayloadAction<ToolFixture>) => {
      state.createToolFixtureLoading = false;
      state.toolFixtures.unshift(action.payload);
      state.pagination.totalCount += 1;
    },
    createToolFixtureFailure: (state, action: PayloadAction<string>) => {
      state.createToolFixtureLoading = false;
      state.createToolFixtureError = action.payload;
    },

    // Update Tool Fixture Actions
    updateToolFixtureStart: (state, action: PayloadAction<UpdateToolFixtureRequest>) => {
      state.updateToolFixtureLoading = true;
      state.updateToolFixtureError = null;
    },
    updateToolFixtureSuccess: (state, action: PayloadAction<ToolFixture>) => {
      state.updateToolFixtureLoading = false;
      const index = state.toolFixtures.findIndex(tf => tf.Id === action.payload.Id);
      if (index !== -1) {
        state.toolFixtures[index] = action.payload;
      }
      if (state.selectedToolFixture?.Id === action.payload.Id) {
        state.selectedToolFixture = action.payload;
      }
    },
    updateToolFixtureFailure: (state, action: PayloadAction<string>) => {
      state.updateToolFixtureLoading = false;
      state.updateToolFixtureError = action.payload;
    },

    // Delete Tool Fixture Actions
    deleteToolFixtureStart: (state, action: PayloadAction<string>) => {
      state.deleteToolFixtureLoading = true;
      state.deleteToolFixtureError = null;
    },
    deleteToolFixtureSuccess: (state, action: PayloadAction<string>) => {
      state.deleteToolFixtureLoading = false;
      state.toolFixtures = state.toolFixtures.filter(tf => tf.Id !== action.payload);
      state.pagination.totalCount -= 1;
      state.deleteDialogOpen = false;
      state.toolFixtureToDelete = null;
    },
    deleteToolFixtureFailure: (state, action: PayloadAction<string>) => {
      state.deleteToolFixtureLoading = false;
      state.deleteToolFixtureError = action.payload;
    },

    // Calibration Events Actions
    fetchCalibrationEventsStart: (state, action: PayloadAction<string>) => {
      state.calibrationEventsLoading = true;
      state.calibrationEventsError = null;
    },
    fetchCalibrationEventsSuccess: (state, action: PayloadAction<CalibrationCalendarDetail[]>) => {
      state.calibrationEventsLoading = false;
      state.calibrationEvents = action.payload;
    },
    fetchCalibrationEventsFailure: (state, action: PayloadAction<string>) => {
      state.calibrationEventsLoading = false;
      state.calibrationEventsError = action.payload;
    },

    // Create Calibration Actions
    createCalibrationStart: (state, action: PayloadAction<CreateCalibrationRequest>) => {
      state.createCalibrationLoading = true;
      state.createCalibrationError = null;
    },
    createCalibrationSuccess: (state, action: PayloadAction<CalibrationCalendarDetail>) => {
      state.createCalibrationLoading = false;
      state.calibrationEvents.unshift(action.payload);
      state.calibrationDialogOpen = false;
    },
    createCalibrationFailure: (state, action: PayloadAction<string>) => {
      state.createCalibrationLoading = false;
      state.createCalibrationError = action.payload;
    },

    // Update Calibration Actions
    updateCalibrationStart: (state, action: PayloadAction<UpdateCalibrationRequest>) => {
      state.updateCalibrationLoading = true;
      state.updateCalibrationError = null;
    },
    updateCalibrationSuccess: (state, action: PayloadAction<CalibrationCalendarDetail>) => {
      state.updateCalibrationLoading = false;
      const index = state.calibrationEvents.findIndex(ce => ce.Id === action.payload.Id);
      if (index !== -1) {
        state.calibrationEvents[index] = action.payload;
      }
      state.calibrationDialogOpen = false;
      state.selectedCalibrationEvent = null;
    },
    updateCalibrationFailure: (state, action: PayloadAction<string>) => {
      state.updateCalibrationLoading = false;
      state.updateCalibrationError = action.payload;
    },

    // Delete Calibration Actions
    deleteCalibrationStart: (state, action: PayloadAction<string>) => {
      state.deleteCalibrationLoading = true;
      state.deleteCalibrationError = null;
    },
    deleteCalibrationSuccess: (state, action: PayloadAction<string>) => {
      state.deleteCalibrationLoading = false;
      state.calibrationEvents = state.calibrationEvents.filter(ce => ce.Id !== action.payload);
    },
    deleteCalibrationFailure: (state, action: PayloadAction<string>) => {
      state.deleteCalibrationLoading = false;
      state.deleteCalibrationError = action.payload;
    },

    // Dropdown Actions
    fetchDropdownsStart: (state) => {
      state.dropdownsLoading = true;
      state.dropdownsError = null;
    },
    fetchDropdownsSuccess: (state, action: PayloadAction<{
      suppliers?: Supplier[];
      departments?: Department[];
      toolTypes?: ToolType[];
    }>) => {
      state.dropdownsLoading = false;
      if (action.payload.suppliers) state.suppliers = action.payload.suppliers;
      if (action.payload.departments) state.departments = action.payload.departments;
      if (action.payload.toolTypes) state.toolTypes = action.payload.toolTypes;
    },
    fetchDropdownsFailure: (state, action: PayloadAction<string>) => {
      state.dropdownsLoading = false;
      state.dropdownsError = action.payload;
    },

    // Statistics Actions
    fetchStatisticsStart: (state) => {
      state.statisticsLoading = true;
      state.statisticsError = null;
    },
    fetchStatisticsSuccess: (state, action: PayloadAction<ToolFixtureStatistics>) => {
      state.statisticsLoading = false;
      state.statistics = action.payload;
    },
    fetchStatisticsFailure: (state, action: PayloadAction<string>) => {
      state.statisticsLoading = false;
      state.statisticsError = action.payload;
    },

    // Search and Filter Actions
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.pagination.pageNum = 1;
    },
    setCalibrationRequiredFilter: (state, action: PayloadAction<boolean | undefined>) => {
      state.filters.calibrationRequired = action.payload;
      state.pagination.pageNum = 1;
    },
    setActiveFilter: (state, action: PayloadAction<boolean | undefined>) => {
      state.filters.active = action.payload;
      state.pagination.pageNum = 1;
    },
    setSupplierFilter: (state, action: PayloadAction<string | undefined>) => {
      state.filters.supplierId = action.payload;
      state.pagination.pageNum = 1;
    },
    setDepartmentFilter: (state, action: PayloadAction<string | undefined>) => {
      state.filters.departmentId = action.payload;
      state.pagination.pageNum = 1;
    },
    setToolTypeFilter: (state, action: PayloadAction<string | undefined>) => {
      state.filters.toolTypeId = action.payload;
      state.pagination.pageNum = 1;
    },

    // Pagination Actions
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.pageNum = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.pageNum = 1;
    },

    // UI Actions
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    setDetailModalOpen: (state, action: PayloadAction<boolean>) => {
      state.detailModalOpen = action.payload;
    },
    setDeleteDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.deleteDialogOpen = action.payload;
    },
    setCalibrationDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.calibrationDialogOpen = action.payload;
    },
    setToolFixtureToDelete: (state, action: PayloadAction<ToolFixture | null>) => {
      state.toolFixtureToDelete = action.payload;
    },
    setSelectedCalibrationEvent: (state, action: PayloadAction<CalibrationCalendarDetail | null>) => {
      state.selectedCalibrationEvent = action.payload;
    },

    // Clear Actions
    clearError: (state) => {
      state.toolFixturesError = null;
      state.selectedToolFixtureError = null;
      state.createToolFixtureError = null;
      state.updateToolFixtureError = null;
      state.deleteToolFixtureError = null;
      state.calibrationEventsError = null;
      state.createCalibrationError = null;
      state.updateCalibrationError = null;
      state.deleteCalibrationError = null;
      state.dropdownsError = null;
      state.statisticsError = null;
    },
    resetToolFixtureState: (state) => {
      state.selectedToolFixture = null;
      state.validationErrors = [];
      state.detailModalOpen = false;
      state.deleteDialogOpen = false;
      state.calibrationDialogOpen = false;
      state.toolFixtureToDelete = null;
      state.selectedCalibrationEvent = null;
    },
  },
});

// Export actions
export const {
  // Tool Fixtures
  fetchToolFixturesStart,
  fetchToolFixturesSuccess,
  fetchToolFixturesFailure,
  fetchToolFixtureByIdStart,
  fetchToolFixtureByIdSuccess,
  fetchToolFixtureByIdFailure,
  createToolFixtureStart,
  createToolFixtureSuccess,
  createToolFixtureFailure,
  updateToolFixtureStart,
  updateToolFixtureSuccess,
  updateToolFixtureFailure,
  deleteToolFixtureStart,
  deleteToolFixtureSuccess,
  deleteToolFixtureFailure,
  
  // Calibration
  fetchCalibrationEventsStart,
  fetchCalibrationEventsSuccess,
  fetchCalibrationEventsFailure,
  createCalibrationStart,
  createCalibrationSuccess,
  createCalibrationFailure,
  updateCalibrationStart,
  updateCalibrationSuccess,
  updateCalibrationFailure,
  deleteCalibrationStart,
  deleteCalibrationSuccess,
  deleteCalibrationFailure,
  
  // Dropdowns & Statistics
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  fetchStatisticsStart,
  fetchStatisticsSuccess,
  fetchStatisticsFailure,
  
  // Search & Filter
  setSearchTerm,
  setCalibrationRequiredFilter,
  setActiveFilter,
  setSupplierFilter,
  setDepartmentFilter,
  setToolTypeFilter,
  setPage,
  setPageSize,
  
  // UI
  setValidationErrors,
  clearValidationErrors,
  setDetailModalOpen,
  setDeleteDialogOpen,
  setCalibrationDialogOpen,
  setToolFixtureToDelete,
  setSelectedCalibrationEvent,
  clearError,
  resetToolFixtureState,
} = toolFixtureSlice.actions;

// Export reducer
export default toolFixtureSlice.reducer; 